import { useState, useRef } from "react";
import { motion, useMotionValue, useTransform, PanInfo, AnimatePresence } from "framer-motion";
import { ArrowLeftRight, ChevronDown, ChevronUp, Check, Trash2, Circle, CheckCircle } from "lucide-react";

export interface GroceryItemData {
  id: string;
  name: string;
  category: string;
  evidenceClaim: string;
  score: number;
  completed: boolean;
}

interface GroceryItemProps {
  item: GroceryItemData;
  onSwap: (itemId: string) => void;
  onComplete: (itemId: string) => void;
  onDelete: (itemId: string) => void;
  isGuestView?: boolean;
}

export const GroceryItem = ({ item, onSwap, onComplete, onDelete, isGuestView = false }: GroceryItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showStrikethrough, setShowStrikethrough] = useState(false);
  const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const x = useMotionValue(0);
  const background = useTransform(x, [0, 150], ["transparent", "hsl(142 76% 36%)"]);
  const checkOpacity = useTransform(x, [0, 100], [0, 1]);
  const checkScale = useTransform(x, [0, 100], [0.5, 1]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    const containerWidth = 300;
    const dragPercentage = (info.offset.x / containerWidth) * 100;

    if (dragPercentage > 40 && !isGuestView && !isCompleting) {
      triggerCompletion();
    }
  };

  const triggerCompletion = () => {
    if (isCompleting) return;
    
    // Step 1: Show visual change (strikethrough + fade)
    setIsCompleting(true);
    setShowStrikethrough(true);
    
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    // Step 2: Wait 500ms, then trigger reorder
    completionTimeoutRef.current = setTimeout(() => {
      onComplete(item.id);
    }, 500);
  };

  const handleCheckboxClick = () => {
    if (isGuestView || item.completed || isCompleting) return;
    triggerCompletion();
  };

  // Completed item view
  if (item.completed) {
    return (
      <motion.div
        layout
        layoutId={item.id}
        initial={false}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 0.3 }}
        className="bg-card rounded-2xl p-4 border border-border"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
            <Check className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="text-foreground line-through flex-1">{item.name}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      layout
      layoutId={item.id}
      className="relative overflow-hidden rounded-2xl gesture-safe-inset"
    >
      {/* Swipe reveal background */}
      <motion.div
        style={{ background }}
        className="absolute inset-0 flex items-center px-6"
      >
        <motion.div 
          style={{ opacity: checkOpacity, scale: checkScale }}
          className="flex items-center gap-2"
        >
          <Check className="w-8 h-8 text-white" />
          <span className="text-white font-medium">Complete</span>
        </motion.div>
      </motion.div>

      <motion.div
        drag={isGuestView || isCompleting ? false : "x"}
        dragConstraints={{ left: 0, right: 150 }}
        dragElastic={{ left: 0, right: 0.2 }}
        onDragEnd={handleDragEnd}
        style={{ x }}
        animate={isCompleting ? { opacity: 0.6 } : { x: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-card rounded-2xl border border-border relative z-10"
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Checkbox */}
            <button
              onClick={handleCheckboxClick}
              disabled={isGuestView || isCompleting}
              className={`mt-0.5 transition-colors ${isGuestView || isCompleting ? 'opacity-50 cursor-not-allowed' : 'hover:text-primary'}`}
            >
              {isCompleting ? (
                <CheckCircle className="w-6 h-6 text-primary" />
              ) : (
                <Circle className="w-6 h-6 text-muted-foreground" />
              )}
            </button>

            <div className="flex-1">
              <button
                onClick={() => !isCompleting && setExpanded(!expanded)}
                className="flex items-center gap-2 text-left w-full"
                disabled={isCompleting}
              >
                <h3 className={`font-medium text-foreground transition-all ${showStrikethrough ? 'line-through opacity-60' : ''}`}>
                  {item.name}
                </h3>
                {expanded ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
              <p className={`text-sm text-muted-foreground mt-1 ${showStrikethrough ? 'opacity-60' : ''}`}>
                {item.category}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {!isGuestView && (
                <button
                  onClick={() => !isCompleting && onDelete(item.id)}
                  disabled={isCompleting}
                  className="w-9 h-9 rounded-xl bg-destructive/10 flex items-center justify-center hover:bg-destructive/20 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </button>
              )}
              <button
                onClick={() => !isGuestView && !isCompleting && onSwap(item.id)}
                disabled={isGuestView || isCompleting}
                className={`w-9 h-9 rounded-xl bg-secondary flex items-center justify-center transition-colors ${
                  isGuestView || isCompleting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent hover:text-accent-foreground'
                }`}
                title="Swap for alternative"
              >
                <ArrowLeftRight className="w-4 h-4 text-foreground" />
              </button>
            </div>
          </div>

          <AnimatePresence>
            {expanded && !isCompleting && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-border overflow-hidden"
              >
                {/* Evidence container - distinct visual for "Why this?" */}
                <div className="evidence-container">
                  <p className="text-sm leading-relaxed">
                    <span className="font-bold text-primary">Why this?</span>
                    <span className="text-foreground ml-1">{item.evidenceClaim}</span>
                  </p>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="h-2 flex-1 bg-secondary rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(item.score, 100)}%` }}
                      className="h-full gradient-primary rounded-full"
                    />
                  </div>
                  <span className="text-sm font-semibold text-primary">
                    {item.score}%
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!isGuestView && !isCompleting && (
          <div className="px-4 pb-2 text-xs text-muted-foreground">
            Swipe right to complete →
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};
